<template>
    <Tabs
        :selected-user="selectedUser"
        :personal-team="userPersonalTeam"
        tab-key="user-teams"
    >
        <DataTable
            class="mt-12"
            :title="selectedUser.name + `'s Teams `"
            searchable
            :headers="headers"
            :items="userTeams"
        >
            <template #item-joined_at="{ data }">
                <div class="flex justify-center p-6">
                    <div v-if="data">
                        {{ format(new Date(data as string), 'd. L. yyyy') }}
                    </div>
                </div>
            </template>

            <template #item-custom="{ row }">
                <div class="flex w-full justify-center gap-2">
                    <AtomicButton
                        color="white"
                        size="sm"
                        @click="handleShowTeam(row as ICurrentTeam)"
                    >
                        <template #icon>
                            <MagnifyingGlassIcon class="h-6 w-6" />
                        </template>
                    </AtomicButton>

                    <AtomicButton
                        v-if="row.bucket_blocked_at === null"
                        v-tooltip="{
                            content: 'Block storage',
                            theme: 'info-tooltip',
                        }"
                        color="red"
                        size="sm"
                        @click="makeBucketPrivate(row as ICurrentTeam)"
                    >
                        <template #icon>
                            <FireIcon class="h-6 w-6" />
                        </template>
                    </AtomicButton>

                    <AtomicButton
                        v-if="row.bucket_blocked_at !== null"
                        v-tooltip="{
                            content: 'Unblock storage',
                            theme: 'info-tooltip',
                        }"
                        color="yellow"
                        size="sm"
                        @click="makeBucketPublic(row as ICurrentTeam)"
                    >
                        <template #icon>
                            <SparklesIcon class="h-6 w-6" />
                        </template>
                    </AtomicButton>
                </div>
            </template>
        </DataTable>
    </Tabs>
</template>
<script lang="ts" setup>
import IDataTableHeader from '@/components/Atomic/DataTable/types/IDataTableHeader';
import IPaginationData from '@/components/Atomic/DataTable/types/IPaginationData';
import ICurrentTeam from '@/types/Members/ICurrentTeam';
import IUser from '@/types/Members/IUser';
import DataTable from '@/components/Atomic/DataTable/DataTable.vue';
import { format } from 'date-fns';

import {
    MagnifyingGlassIcon,
    FireIcon,
    SparklesIcon,
} from '@heroicons/vue/24/outline';

import Tabs from '../components/UserTabs.vue';
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import AtomicButton from '@/components/Atomic/Buttons/AtomicButton.vue';
import { create } from '@composables/useNotifications';
import useConfirm from '@composables/useConfirm';
const { show } = useConfirm();

defineProps<{
    selectedUser: IUser;
    userTeams: IPaginationData<ICurrentTeam>;
    userPersonalTeam: ICurrentTeam;
}>();

const headers: IDataTableHeader[] = [
    {
        text: 'Name',
        id: 'name',
        sortable: true,
        sortableId: 'name',
    },
    {
        text: 'Role',
        id: 'role',
        sortable: true,
        sortableId: 'role',
    },
    {
        text: 'Joined at',
        id: 'joined_at',
        align: 'center',
    },
    {
        text: 'Storage blocked at',
        id: 'bucket_blocked_at',
        align: 'center',
    },
    {
        text: '',
        id: 'custom',
    },
];

const handleShowTeam = function (team: ICurrentTeam) {
    router.get(
        route('kiosk.teams.feature-management.index', {
            team: team.id,
        })
    );
};

const makeBucketPrivate = async (team: ICurrentTeam) => {
    const confirmed = await show({
        header: 'Block storage for the selected Team',
        message: `Are you sure you want to block storage for this team?`,
        type: 'danger',
        confirm: 'Block',
    });
    if (confirmed) {
        router.post(
            route('kiosk.teams.make-bucket-private', { team: team.id }),
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Storage has been blocked',
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'Storage has not been blocked',
                        type: 'error',
                    });
                },
            }
        );
    }
};

const makeBucketPublic = async (team: ICurrentTeam) => {
    const confirmed = await show({
        header: 'Unblock storage for the selected Team',
        message: `Are you sure you want to unblock storage for this team?`,
        type: 'danger',
        confirm: 'Unblock',
    });
    if (confirmed) {
        router.post(
            route('kiosk.teams.make-bucket-public', { team: team.id }),
            {},
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Storage has been unblocked',
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'Storage has not been unblocked',
                        type: 'error',
                    });
                },
            }
        );
    }
};
</script>
